import React, { useCallback, useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextPage } from 'next/types';

import {
  guestCheckoutRegistrationToken,
  registerUserWithOTP,
  sendOTP,
  sendOtpWithCaptcha,
  verifyOTP
} from 'src/services/apiRequests/Login/LoginService';
import {
  getActiveSubscription,
  getAllSubsciptionPlans,
  getSubscriptionPlansSelectionData,
  getUpgradePlansSelectionData
} from 'src/services/apiRequests/SubscriptionService';
import { fetchUserData } from 'src/services/apiRequests/UserService';
import { PlansMetaData } from 'src/services/responseTypes/plansMetaDataResponse';
import { SubscriptionApiResponse } from 'src/services/responseTypes/subscriptionApiResponse';
import SimpleStore from 'src/store/SimpleStore';
import { colors } from 'src/styles/colors';
import {
  countryApiTypes,
  Data,
  Option,
  SubscriptionState
} from 'src/types/subscription';
import { mixpanelObject } from 'src/utils/analytics';
import { putFacebookEvent } from 'src/utils/analytics/fbpixel/fbCapiHelper';
import { gtm } from 'src/utils/analytics/gtm';
import {
  MIXPANEL_APP_ARP_USER,
  MIXPANEL_CAROUSAL_ID,
  MIXPANEL_CAROUSAL_NAME,
  MIXPANEL_COHORT_DISCOUNT_PERCENT,
  MIXPANEL_COHORT_ID,
  MIXPANEL_COUPON_CODE,
  MIXPANEL_EMAIL,
  MIXPANEL_MOBILE,
  MIXPANEL_PAGE_NAME,
  MIXPANEL_PAYMENT_JOURNEY,
  MIXPANEL_UNIQUE_ID,
  MIXPANEL_USER_TYPE
} from 'src/utils/analytics/mixpanel/mixpanelConstants';
import {
  pageNameSetter,
  subscriptionPageViewedEvent,
  timeEvent
} from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import {
  appsflyerPropertySetter,
  setGlobalUsersData,
  setLoginInitiatedAndReturnMixPanel,
  userDataPropertySetter
} from 'src/utils/analytics/mixpanel/mixpanelHelper';
import { getUserTokenFromLS } from 'src/utils/auth-utils/TokenHandler';
import {
  deleteLocalStorage,
  getLocalStorage,
  getObjectFromLocalStorage,
  getSessionStorage,
  setCookie,
  setLocalStorage,
  setSessionStorage
} from 'src/utils/browserStorageHandler';
import {
  appsflyerEventPush,
  getTappPlanNew,
  parseJsonObject,
  parseJwt
} from 'src/utils/common';
import { ActivePromotions } from 'src/utils/commonTypes';
import { COUNTRY_CODE_LIST } from 'src/utils/constants/ApiConstants';
import { GUEST_TOKEN, REFEREE } from 'src/utils/constants/AppConstants';
import { PAGE_NAME_CONSTANTS } from 'src/utils/constants/ConsumptionPagesConstants';
import { USER_TYPE_COOKIE } from 'src/utils/constants/cookieConstants';
import { HTTP_HEADER_KEYS } from 'src/utils/constants/HttpMethodsConstants';
import {
  EXPIRES_IN,
  PARTNER_NAME,
  PAYMENT_JOURNEY,
  REFERRAL_CODE,
  REFERRAL_ID,
  REFERRAL_NAME,
  REFERRAL_USERID,
  REFRESH_TOKEN,
  SELECTED_PLAN,
  SETTINGS,
  TAPPP_ERROR_FLAG,
  TAPP_OBJECT,
  USER_TOKEN,
  USER_TYPE,
  VISITED_PAYMENT_PAGE
} from 'src/utils/constants/localStorageConstants';
import { META_EVENTS } from 'src/utils/constants/metaPixelConstants';
import {
  APP_USER,
  COHORT_CAMPAIGN_CONSTANTS,
  CountryCode,
  LANDING_ON_SUBSCRIBER_PLANS_SCREEN,
  ROUTES,
  SUBSCRIPTION_PAGE_DESCRIPTION,
  SUBSCRIPTION_PAGE_DESCRIPTION_GLOBAL,
  SUBSCRIPTION_PAGE_HEADER,
  SUBSCRIPTION_PAGE_HEADER_GLOBAL,
  UserTypes
} from 'src/utils/constants/stringConstants';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { fetchEntitlementUserType } from 'src/utils/fetchEntitlementUserType';
import { WINDOW_LISTNER_EVENTS } from 'src/utils/GlobalListners/WindowListnerConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';
import { fbqTrack, loadScripts } from 'src/utils/scripts/thirdPartyScripts';
import {
  buyPlanNew,
  getPlanWithCohortNew,
  getReferralParameters,
  setGlobalPaymentProviders,
  setPlansForUpgrade,
  setReferralParametersInSession,
  transformPlanCategories,
  validateReferee
} from 'src/utils/SubscriptionHelper/SubscriptionHelper';

import { SubscriptionProvider } from './SubscriptionContext';
import SubscriptioV3 from './SubscriptionV3';
import { useAppStore } from '../../hooks/useAppStore';
import { Plan } from '../../services/responseTypes/displayPlansResponse';
import { MediaBP as MQ } from '../../styles/mediaQuery';
import Modal from '../modal/Modal';
import ToastMessage from '../toastMessage';

interface verifyOTPResponse {
  code?: number;
  message?: string;
  access_token?: string;
  expires_in?: number;
  token_type?: string;
  refresh_token?: string;
}

const EnterOtp = dynamic(
  () => import('../../components/loginPopup/enterOTP/EnterOtp'),
  {
    loading: () => null
  }
);
const LoginPopUp = dynamic(
  () => import('../../components/loginPopup/LoginPopup'),
  {
    loading: () => null
  }
);

const SubscriptionComponentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  & > * {
    z-index: 1;
  }
  padding-top: 0px;
  ${MQ.MediaSm} {
    max-width: 100%;
    width: 100%;
  }
  ${MQ.MediaMd} {
    max-width: 840px;
  }
  ${MQ.MediaLg} {
    width: 100%;
  }
`;
type Props = {
  plansDataNew: { data: Data };
};

const SubscriptionWrapperNew: NextPage<Props> = (props) => {
  const {
    translations,
    usersLocationData,
    appConfig,
    usersData,
    setUsersData,
    plansMetaData: MetaDataPlans,
    subscriptionData,
    setSubscriptionData,
    loadExternalScripts,
    setIsUserLoggedIn,
    isUserLoggedIn,
    setExternalScripts,
    setPopups,
    remoteConfig,
    popups: { showReferralSubscriptionNudge = false }
  } = useAppStore();

  const isFeatureReferral = isFeatureEnabled(
    remoteConfig,
    FeatureFlagConstants.CONFIG_REFERRAL
  );

  const NEW_SUBSCRIPTION_STRINGS = translations.subscription;
  const countryCode = usersLocationData?.country_code || '';
  const router = useRouter();
  const currentSubscription = React.useRef<SubscriptionApiResponse | null>(
    null
  );
  const plansMetaData = React.useRef<PlansMetaData[]>(MetaDataPlans || []);
  let pageTitle = '';
  let pageDescription = '';
  if (countryCode && countryCode !== 'IN') {
    pageTitle = SUBSCRIPTION_PAGE_HEADER_GLOBAL;
    pageDescription = SUBSCRIPTION_PAGE_DESCRIPTION_GLOBAL;
  } else {
    pageTitle = SUBSCRIPTION_PAGE_HEADER;
    pageDescription = SUBSCRIPTION_PAGE_DESCRIPTION;
  }
  const trackMixpanelEvents = (uniqueID: string | undefined) => {
    mixpanelObject.setProperty(
      MIXPANEL_PAGE_NAME,
      PAGE_NAME_CONSTANTS.PACK_SELECTION
    );
    mixpanelObject.setProperty(MIXPANEL_CAROUSAL_ID, 'N/A');
    mixpanelObject.setProperty(MIXPANEL_CAROUSAL_NAME, 'N/A');
    mixpanelObject.setProperty(MIXPANEL_UNIQUE_ID, uniqueID);
    // This is for when user comes from app to web for payment
    const paymentJourneySource = getLocalStorage(PAYMENT_JOURNEY);
    if (paymentJourneySource && paymentJourneySource === APP_USER) {
      mixpanelObject.setProperty(
        MIXPANEL_PAYMENT_JOURNEY,
        MIXPANEL_APP_ARP_USER
      );
    }
    pageNameSetter(mixpanelObject);
    setGlobalUsersData(mixpanelObject, usersData, usersLocationData);
    subscriptionPageViewedEvent();
    timeEvent();
  };

  const setMixpanelUserType = () => {
    mixpanelObject.setProperty(MIXPANEL_USER_TYPE, getLocalStorage(USER_TYPE));
  };
  const [plansData, setPlansData] = useState<Data | null>(
    props.plansDataNew?.data
  );
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputType, setInputType] = useState('');
  const [errorMessage, setErrorMessage] = useState();
  const [otpErrorMessage, setOtpErrorMessage] = useState<string | undefined>();
  const [countryCodeData, setCountryCodeData] = useState();
  const [userType, setUserType] = useState(0);
  const [isPrepaidCouponLogin, setIsPrepaidCouponLogin] = useState(false);
  const [triggerApplyCouponCodePopup, setTriggerApplyCouponCodePopup] =
    useState(false);
  const [upgradeApiFail, setUpgradeApiFail] = useState<boolean>(false);
  const isPageViewedEventFired = useRef(false);
  const refExitNudgeFlag = useRef(false);
  const refExitNudgePushFlagFlag = useRef(false);
  const [toastMsg, setToastMsg] = useState(false);
  const { planCategories: initialPlanCategories = [] } = plansData || {};

  /**
   * Resends the OTP
   * @param inputType email or phone
   * @param inputValue emai id or phone number
   */
  const resendOTP = async (
    inputType: string,
    inputValue: string,
    googleCaptchaValue: string | null | undefined
  ) => {
    if (googleCaptchaValue && userType !== 0) {
      await sendOtpWithCaptcha(inputType, inputValue, googleCaptchaValue);
    } else {
      await sendOTP(inputType, inputValue);
    }
  };

  /**
   * handles otp verification response
   * @param data response data from api
   */
  const handleOTPVerification = async (data: verifyOTPResponse) => {
    let method;
    if (inputType === 'phoneno') {
      method = MIXPANEL_MOBILE;
    } else {
      method = MIXPANEL_EMAIL;
    }
    if (data?.access_token) {
      const userId = parseJwt(data.access_token).user_id;
      // got the auth token
      setLocalStorage(USER_TOKEN, data.access_token);
      SimpleStore.set(HTTP_HEADER_KEYS.AUTHORIZATION, data?.access_token);
      await userFetchMixpanelSet();
      gtm.otpVerificationSuccessfulEventGTM();
      setLocalStorage(REFRESH_TOKEN, data.refresh_token);
      const expiresIn =
        Math.floor(new Date().getTime()) + Number(data.expires_in);
      setLocalStorage(EXPIRES_IN, expiresIn);
      setPlansData(null);
      setIsUserLoggedIn(true);
      if (
        isFeatureEnabled(
          remoteConfig,
          FeatureFlagConstants.FEATURE_META_PIXEL_INTEGRATION
        )
      ) {
        if (countryCode !== 'IN') {
          fbqTrack(META_EVENTS.OTPVerified);
          putFacebookEvent(
            META_EVENTS.OTPVerified,
            new Date().getTime(),
            window.location.href
          );
        }
        fbqTrack(META_EVENTS.Completeregistration);
        putFacebookEvent(
          META_EVENTS.Completeregistration,
          new Date().getTime(),
          window.location.href
        );
      }
      setLoginInitiatedAndReturnMixPanel(
        'result',
        mixpanelObject,
        method,
        '',
        true,
        '',
        userId
      );
    }
    if (data?.code === 2) {
      // code is invalid or expired
      setOtpErrorMessage(data.message);
      setIsUserLoggedIn(false);
      setLoginInitiatedAndReturnMixPanel(
        'result',
        mixpanelObject,
        method,
        '',
        false,
        data.message
      );
    }
  };

  /**
   * This function will be useful to fetch userdata and location when user is logging from subscription page
   */
  const userFetchMixpanelSet = async () => {
    // user data fetching
    const userData = await fetchUserData();
    userDataPropertySetter(mixpanelObject, userData);
    setGlobalUsersData(mixpanelObject, userData, usersLocationData);
    setUsersData({ ...userData });
  };

  /**
   * Verifies the otp
   * @param otp OTP value
   * @param inputType email or phoneno
   * @param inputValue email id or phone number
   */
  const handleVerifyOtp = async (
    otp: string,
    inputType: string,
    inputValue: string
  ) => {
    let method;
    if (inputType === 'phoneno') {
      method = MIXPANEL_MOBILE;
    } else {
      method = MIXPANEL_EMAIL;
    }
    try {
      if (userType === 0) {
        const data = await verifyOTP(otp, inputType, inputValue);
        handleOTPVerification(data);
      } else {
        const data = await registerUserWithOTP(otp, inputType, inputValue);
        handleOTPVerification(data);
      }
    } catch (error: any) {
      const errorObj = await error?.res.json();
      const errorMessage = errorObj?.message || ''; // passing all types error code messages not only errorObj?.code === 2
      setOtpErrorMessage(errorMessage);
      setLoginInitiatedAndReturnMixPanel(
        'result',
        mixpanelObject,
        method,
        '',
        false,
        errorObj.message
      );
    }
  };

  /**
   * Handles whether password or otp login should popup
   * @param inputValue email id or mobile number
   * @param inputType email or mobile
   */

  const handleContinue = useCallback(
    (inputValue: string, inputType: string, captchaValue: string) => {
      setOtpLoading(true);
      const loginType = inputType === 'phoneno' ? 'mobile' : 'email';
      setInputValue(inputValue);
      setInputType(inputType);
      guestCheckoutRegistrationToken(loginType, inputValue)
        .then(async (data) => {
          // check if the user is verified or not
          if (data.code === 0) {
            //user is verified
            setShowLoginPopup(false);
            setShowOtpPopup(true);
            setOtpLoading(false);
            await sendOTP(inputType, inputValue);
          } else if (data.code === 1) {
            //user is not verified
            setUserType(1);
            setShowLoginPopup(false);
            setShowOtpPopup(true);
            setOtpLoading(false);
            if (captchaValue) {
              sendOtpWithCaptcha(inputType, inputValue, captchaValue);
            } else {
              await sendOTP(inputType, inputValue);
            }
          } else {
            setErrorMessage(data.message);
          }
        })
        .catch((err) => console.error('err gettoken', err));
    },
    [inputValue, inputType]
  );

  /**
   * handles the login close button
   */
  const closeLoginPopup = useCallback(() => {
    setShowLoginPopup(false);
    setErrorMessage(undefined);
    if (localStorage.getItem(USER_TOKEN)) {
      setIsUserLoggedIn(true);
    }
  }, []);

  /**
   * handles the otp close button
   */
  const closeOtpPopup = useCallback(() => {
    setOtpErrorMessage('');
    setShowOtpPopup(false);
  }, [setOtpErrorMessage, setShowOtpPopup]);

  const premiumClickHandler = (prepaidCouponValue = '') => {
    setShowLoginPopup(true);
    if (prepaidCouponValue) setIsPrepaidCouponLogin(true);
  };

  /**
   * handles the click of edit email or number
   */
  const handleEditInput = () => {
    setShowOtpPopup(false);
    setShowLoginPopup(true);
    setOtpErrorMessage('');
  };

  const setCohortProperties = (planWithCohort: Option | null) => {
    const {
      price: { code: cohortPromoCode = 'N/A', discountPercentage = 'N/A' } = {}
    } = planWithCohort || {};

    mixpanelObject.setProperty(
      MIXPANEL_COHORT_ID,
      Object.values(COHORT_CAMPAIGN_CONSTANTS).find((campaignCode) =>
        cohortPromoCode.includes(campaignCode)
      ) || 'N/A'
    );
    mixpanelObject.setProperty(MIXPANEL_COUPON_CODE, cohortPromoCode || 'N/A');
    mixpanelObject.setProperty(
      MIXPANEL_COHORT_DISCOUNT_PERCENT,
      discountPercentage || 'N/A'
    );
  };

  //fetching the country codes
  useEffect(() => {
    if (!countryCodeData && showLoginPopup) {
      fetch(COUNTRY_CODE_LIST)
        .then((res) => res.json())
        .then((data) => {
          const formattedData = data.map((list: countryApiTypes) => ({
            validMobileDigits: list?.valid_mobile_digits,
            validMobileDigitMax: list?.valid_mobile_digits_max,
            value: list?.code,
            phoneCode: list?.['phone-code'],
            mobileRegistration: list?.mobile_registration,
            mobileRegistrationWithOtp: list?.mobile_registration_with_otp,
            label: `${list?.name}`
          }));
          setCountryCodeData(formattedData);
        })
        .catch((err) => console.error('error while fetching', err));
    }
  }, [showLoginPopup]);

  useEffect(() => {
    appConfig.pageType = 'subscriptionPage';
    actionsPostPlansData();
  }, [usersData]);

  useEffect(() => {
    const userType = getLocalStorage(USER_TYPE);
    const parterName = getLocalStorage(PARTNER_NAME);
    let popstateHandler: () => void;
    if (
      isFeatureReferral &&
      !refExitNudgeFlag.current &&
      !showReferralSubscriptionNudge &&
      isUserLoggedIn &&
      userType === UserTypes.REGISTER &&
      !parterName
    ) {
      const popstateHandler = () => {
        setPopups({
          showReferralSubscriptionNudge: true,
          referralSourceElement: 'Back_Button'
        });
        window.removeEventListener(
          WINDOW_LISTNER_EVENTS.POPSTATE,
          popstateHandler
        );
      };

      window.addEventListener(WINDOW_LISTNER_EVENTS.POPSTATE, popstateHandler);
      if (!refExitNudgePushFlagFlag.current) {
        window.history.pushState({ page: 'Subscription' }, '', router.asPath);
        refExitNudgePushFlagFlag.current = true;
      }
      refExitNudgeFlag.current = true;
    }
    return () => {
      window.removeEventListener(
        WINDOW_LISTNER_EVENTS.POPSTATE,
        popstateHandler
      );
      refExitNudgeFlag.current = false;
    };
  }, [isUserLoggedIn]);

  useEffect(() => {
    if (!countryCode) return;
    if (
      isFeatureEnabled(
        remoteConfig,
        FeatureFlagConstants.FEATURE_META_PIXEL_INTEGRATION
      )
    ) {
      if (countryCode !== 'IN') {
        fbqTrack(META_EVENTS.MetaSubscriptionPage);
        putFacebookEvent(
          META_EVENTS.MetaSubscriptionPage,
          new Date().getTime(),
          window.location.href
        );
      }
    }
    getDisplayPlans();
  }, [countryCode]);

  useEffect(() => {
    if (plansData) {
      actionsPostPlansData();
    } else {
      if (getLocalStorage(USER_TOKEN) && !isUserLoggedIn) {
        setIsUserLoggedIn(true);
      }
      getDisplayPlans();
    }
  }, [JSON.stringify(plansData), usersData, subscriptionData]);

  const actionsPostPlansData = () => {
    if (plansData) {
      //calls tappp flow
      getPlansMetaData();
      setPlansForUpgrade(plansData, currentSubscription.current?.id);
      // tappFlow();
      if (!isPageViewedEventFired.current) {
        const cohortPlan = getPlanWithCohortNew(plansData);

        setCohortProperties(cohortPlan);

        if (!isUserLoggedIn) {
          const guestToken = getLocalStorage(GUEST_TOKEN);
          trackMixpanelEvents(guestToken);
          isPageViewedEventFired.current = true;
        } else if (usersData && subscriptionData) {
          trackMixpanelEvents(usersData?.id);
          isPageViewedEventFired.current = true;
        }
      }

      setTimeout(() => {
        if (!loadExternalScripts) {
          loadScripts(countryCode);
          setExternalScripts(true);
        }
        triggerAppsFlyerEvent();
      }, 7 * 1000);
    }
  };

  /**
   * This handles when selected plan id is available to user after login and they are allowed to purchase it,
   * we redirect the user to payment page, otherwise, keeps on subsription page.
   */
  useEffect(() => {
    if (isUserLoggedIn && !isPrepaidCouponLogin && plansData) {
      const visitedPaymentPage =
        getSessionStorage(VISITED_PAYMENT_PAGE) || false;
      const selectedPlanData = getObjectFromLocalStorage(SELECTED_PLAN);
      if (
        !currentSubscription?.current?.id &&
        plansData?.planCategories
          .flatMap((plan) => plan.options)
          .some(
            (plan) =>
              plan.id == selectedPlanData?.pack_id && plan.purchaseAllowed
          ) &&
        !visitedPaymentPage
      ) {
        // Check if Referral feature is enabled
        // Check if user is referee
        // Check if referrer and referee is same user, then be on the same page and show toast message
        const isSelfReferee = isFeatureReferral && validateReferee();
        if (isSelfReferee) {
          deleteLocalStorage(SELECTED_PLAN);
          setToastMsg(true);
          setTimeout(() => {
            window.location.href = `/${ROUTES.subscription}`;
          }, 3000);
        } else {
          window.location.href = 'payment';
        }
      } else {
        setShowOtpPopup(false);
      }
    } else if (isUserLoggedIn && showOtpPopup) {
      closeOtpPopup();
    }
  }, [JSON.stringify(plansData), isUserLoggedIn]);

  useEffect(() => {
    // Global subscription promotion handling. Taking paymentProvider details from URL
    if (Object.keys(router.query).length) {
      if (countryCode !== CountryCode.IN) {
        setGlobalPaymentProviders(router.query);
      }
      setReferralParametersInSession(router.query);
    } else {
      const referralId = getLocalStorage(REFERRAL_ID);
      if (referralId) {
        deleteLocalStorage(REFERRAL_ID);
        deleteLocalStorage(REFERRAL_NAME);
        deleteLocalStorage(REFERRAL_CODE);
        deleteLocalStorage(REFERRAL_USERID);
      }
    }
  }, [JSON.stringify(router.query)]);

  useEffect(() => {
    if (!countryCode) return;
    getDisplayPlans();
  }, [countryCode]);

  const tappFlow = () => {
    const tappObject = getLocalStorage(TAPP_OBJECT);
    if (tappObject && plansData?.planCategories?.length) {
      const parseTappObject = parseJsonObject(tappObject);
      const { planId } = parseTappObject;
      const tappSelectedPlan = getTappPlanNew(planId, plansData.planCategories);
      if (tappSelectedPlan?.id === planId) {
        setLocalStorage(SELECTED_PLAN, tappSelectedPlan);
        window.location.href = 'payment';
      } else {
        setSessionStorage(TAPPP_ERROR_FLAG, true);
        window.location.href = '/ctmaerror';
      }
    }
  };

  const triggerAppsFlyerEvent = async () => {
    let subscriptionData: SubscriptionApiResponse[] | null = [];
    if (
      typeof window !== 'undefined' &&
      window.localStorage.getItem(USER_TOKEN)
    ) {
      subscriptionData = await getAllSubsciptionPlans(countryCode);
    }

    const userSettings = getObjectFromLocalStorage(SETTINGS);
    const trackObj = appsflyerPropertySetter(
      { eventName: LANDING_ON_SUBSCRIBER_PLANS_SCREEN },
      usersData,
      usersLocationData,
      userSettings,
      subscriptionData,
      mixpanelObject
    );
    appsflyerEventPush(trackObj);
  };

  const getPlansMetaData = async () => {
    if (!plansData) return;
    tappFlow();
  };

  const getDisplayPlans = async () => {
    const userToken = getUserTokenFromLS();
    // if user is logged in
    if (userToken) {
      const subscriptionDataResponse = await getActiveSubscription(countryCode);
      if (subscriptionDataResponse) {
        setSubscriptionData({
          ...subscriptionData,
          ...subscriptionDataResponse
        });
        const maxPricePack = subscriptionDataResponse.maxPricePack;
        if (maxPricePack) {
          currentSubscription.current = maxPricePack;
          const packType = fetchEntitlementUserType(maxPricePack);
          if (packType) {
            // setUserPackType(packType);
          }
        }
        setLocalStorage(
          USER_TYPE,
          maxPricePack ? UserTypes.PREMIUM : UserTypes.REGISTER
        );
        setCookie(
          USER_TYPE_COOKIE,
          maxPricePack ? UserTypes.PREMIUM : UserTypes.REGISTER
        );
      }
    } else {
      setLocalStorage(USER_TYPE, UserTypes.GUEST);
      setCookie(USER_TYPE_COOKIE, UserTypes.GUEST);
      SimpleStore.set(HTTP_HEADER_KEYS.X_USER_TYPE, UserTypes.GUEST);
    }
    //set mixpanel user type
    setMixpanelUserType();
    // if user is logged in and has a plan
    // find the
    if (currentSubscription?.current?.subscription_plan?.id) {
      const upgradeData = await getUpgradePlansSelectionData({
        planId: currentSubscription?.current?.subscription_plan?.id,
        campaignId: router.query['campaign-id']?.toString()
      });
      if (upgradeData.data) {
        setPlansData(upgradeData.data);
        if (isPrepaidCouponLogin) {
          setTriggerApplyCouponCodePopup(true);
        }
      } else {
        setUpgradeApiFail(true);
      }
    } else {
      // Getting referral params from local storage and passing to display plan API
      const referralData = getReferralParameters();
      const { id, code, name } = referralData;
      if (
        plansData &&
        !userToken &&
        !(name && name.toString().toLowerCase() === REFEREE)
      ) {
        return;
      }
      // Getting referral params from session storage and passing to display plan API
      const timeout = 8_000;
      const displayPlans = await getSubscriptionPlansSelectionData({
        withAuth: true,
        timeout,
        referralId: id,
        code: code,
        discountType: name,
        campaignId: router.query['campaign-id']?.toString()
      });
      if (displayPlans) {
        setPlansData(displayPlans.data);
        if (isPrepaidCouponLogin) {
          setTriggerApplyCouponCodePopup(true);
        }
      }
    }
  };

  const callBuyPlan = (
    plansData: SubscriptionState[] | null,
    currentPlan: Plan | null,
    selectedPlanId: string,
    activePromos: ActivePromotions,
    promoCode: string,
    countryCode: string
  ) => {
    buyPlanNew(
      plansData,
      plansMetaData.current,
      currentPlan,
      selectedPlanId,
      activePromos,
      promoCode,
      countryCode,
      currentSubscription
    );
  };

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="icon" href="/favicon.ico" />
        {/* <link rel="preload" href={props.firstBannerImageUrl} as="image" {...{fetchpriority:'high'}} /> */}
      </Head>

      {NEW_SUBSCRIPTION_STRINGS && (
        <SubscriptionComponentContainer data-test-id="subscriptionComponentContainer">
          <SubscriptionProvider
            initialState={transformPlanCategories(initialPlanCategories)}>
            <SubscriptioV3
              premiumClickHandler={premiumClickHandler}
              buyPlan={callBuyPlan}
              plansData={plansData}
              currentSubscription={currentSubscription.current}
              triggerApplyCouponCodePopup={triggerApplyCouponCodePopup}
            />
          </SubscriptionProvider>
          {showLoginPopup && (
            <Modal
              show={showLoginPopup}
              onClose={closeLoginPopup}
              width="480px"
              height="497px"
              background={colors.white}
              renderCloseIcon={true}
              closeIconColor={colors.black}
              isLoading={otpLoading}
              padding="30px">
              <LoginPopUp
                handleContinue={handleContinue}
                errorMessage={errorMessage}
                onClose={closeLoginPopup}
                countryCodeData={countryCodeData}
              />
            </Modal>
          )}
          {showOtpPopup && (
            <Modal
              show={showOtpPopup}
              width="500px"
              height="500px"
              background={colors.white}
              onClose={closeOtpPopup}
              // isLoading={loadingSpinner}
              renderCloseIcon={true}
              closeIconColor={colors.black}
              padding="30px">
              <EnterOtp
                inputType={inputType}
                inputValue={inputValue}
                show={showOtpPopup}
                onClose={closeOtpPopup}
                otpErrorMessage={otpErrorMessage}
                setOtpErrorMessage={setOtpErrorMessage}
                handleVerifyOtp={handleVerifyOtp}
                resendOTP={resendOTP}
                handleEditInput={handleEditInput}
                userType={userType}
                countryCodeData={countryCodeData}
              />
            </Modal>
          )}
        </SubscriptionComponentContainer>
      )}
      {toastMsg && (
        <ToastMessage
          duration={3000}
          message={NEW_SUBSCRIPTION_STRINGS.Referral_SelfReferral_Toast_Text}
        />
      )}
    </>
  );
};
export default SubscriptionWrapperNew;
